import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {isSessionApprovalMode, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {
  preparedPromptPayloadChars,
  type AhpProvisioningTarget,
  type AhpSpaceSelection,
  type PreparedPrompt,
} from './contracts'
import {createHandoffStorage, DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS, type HandoffFailure} from './handoff-storage'

/**
 * The serializable target for a new One Copilot session. Chat is intentionally
 * a separate variant so it cannot carry a repository that would provision a
 * task. Task repository identifiers are resolved by the destination composer.
 */
export type OneCopilotEntryTarget =
  | {
      kind: 'chat'
      provisioningTarget?: AhpProvisioningTarget
    }
  | {
      kind: 'task'
      repository?: {
        nameWithOwner: string
        branch?: string
      }
      reference?: OneCopilotTaskReference
    }

type LegacyOneCopilotChatTarget = {
  kind: 'chat'
  space: AhpSpaceSelection
}

type StoredOneCopilotEntryTarget = OneCopilotEntryTarget | LegacyOneCopilotChatTarget

/** File/snippet context supported by the current task-composer contract. */
export interface OneCopilotTaskReference {
  kind?: 'file' | 'snippet'
  path: string
  range?: {
    start: number
    end: number
  }
  /** Used when the contextual reference supplies the task repository. */
  repository?: string
}

export type OneCopilotEntryBehavior =
  | {behavior: 'prefill'}
  | {
      behavior: 'submit'
      /** Stable input identity used to make an automatic submit idempotent. */
      inputId: string
    }

export interface OneCopilotStartupConfiguration {
  approvalMode: SessionApprovalMode
}

/**
 * A serializable command for the feature-flagged Copilot Agents route.
 *
 * For example, use `{behavior: 'prefill'}` for a task the user should review,
 * `{behavior: 'submit', inputId}` for an automatic task submit, or
 * `{target: {kind: 'chat'}, behavior: 'prefill'}` for repository-free Chat.
 */
export type OneCopilotEntryRequest = {
  target: OneCopilotEntryTarget
  prompt: PreparedPrompt
  /** Stable analytics provenance; it is not the destination draft namespace. */
  source: string
  /** Configuration applied only while creating the session for this draft. */
  startupConfiguration?: OneCopilotStartupConfiguration
} & OneCopilotEntryBehavior

type StoredOneCopilotEntryRequest = {
  target: StoredOneCopilotEntryTarget
  prompt: PreparedPrompt
  source: string
  startupConfiguration?: OneCopilotStartupConfiguration
} & OneCopilotEntryBehavior

export const ONE_COPILOT_ENTRY_VERSION = 3

export interface OneCopilotEntryHandoff {
  readonly version: typeof ONE_COPILOT_ENTRY_VERSION
  readonly id: string
  readonly createdAt: number
  readonly expiresAt: number
  readonly owner?: string
  readonly request: OneCopilotEntryRequest
}

interface VersionOneCopilotEntryHandoff {
  readonly version: 1
  readonly id: string
  readonly createdAt: number
  readonly expiresAt: number
  readonly owner?: string
  readonly request: StoredOneCopilotEntryRequest
}

interface VersionTwoCopilotEntryHandoff {
  readonly version: 2
  readonly id: string
  readonly createdAt: number
  readonly expiresAt: number
  readonly owner?: string
  readonly request: StoredOneCopilotEntryRequest
}

type StoredOneCopilotEntryHandoff =
  | VersionOneCopilotEntryHandoff
  | VersionTwoCopilotEntryHandoff
  | OneCopilotEntryHandoff

export type OneCopilotEntryFailure = HandoffFailure | 'invalid-request' | 'invalid-destination' | 'navigation'

export type OneCopilotEntryCreationResult =
  | {
      status: 'created'
      id: string
      url: string
    }
  | {
      status: 'failed'
      failure: OneCopilotEntryFailure
    }

export type OneCopilotEntryClaimResult =
  | {status: 'claimed'; id: string; request: OneCopilotEntryRequest}
  | {status: 'empty' | 'unavailable' | 'superseded' | 'invalid' | 'stale'}

export const ONE_COPILOT_AGENTS_PATH = '/copilot/agents'
export const CANONICAL_ONE_COPILOT_AGENTS_PATH = '/copilot'

export function getOneCopilotAgentsPath() {
  return isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
    ? CANONICAL_ONE_COPILOT_AGENTS_PATH
    : ONE_COPILOT_AGENTS_PATH
}
export const ONE_COPILOT_ENTRY_QUERY_PARAM = 'one_copilot'
export const ONE_COPILOT_ENTRY_TTL_MS = 5 * 60 * 1000

/**
 * Sized from the `sessionStorage` quota rather than chosen. See
 * {@link DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS}: an entry that does not fit is
 * refused with `too-large` so the caller can say so, never trimmed to fit.
 */
export const MAX_ONE_COPILOT_ENTRY_PAYLOAD_CHARS = DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS

const ONE_COPILOT_ENTRY_KEY = 'copilot-one-copilot-entry'

export const ONE_COPILOT_ENTRY_FAILURE_MESSAGES: Record<OneCopilotEntryClaimResult['status'], string> = {
  empty: 'This Copilot request is no longer available. Try opening it again.',
  unavailable: 'Copilot could not restore this request because browser storage is unavailable.',
  superseded: 'This Copilot request was replaced by a newer request. Try opening it again.',
  invalid: 'Copilot could not restore this request. Try opening it again.',
  stale: 'This Copilot request expired. Try opening it again.',
  claimed: '',
}

/**
 * What to tell the person who tried to open Copilot when the request could not
 * be created.
 *
 * `too-large` and `storage-full` stay distinct: only the first is proof that
 * *this* request is the problem, so a request with no attachments is never told
 * to remove one.
 */
export const ONE_COPILOT_ENTRY_CREATION_FAILURE_MESSAGES: Record<OneCopilotEntryFailure, string> = {
  'too-large':
    'This request is too large to open in Copilot. Try shortening it or removing an attachment, then try again.',
  'storage-full': 'Copilot could not open this request because browser storage is full. Free up storage and try again.',
  unavailable:
    'Copilot could not open this request because browser storage is unavailable. Check your browser settings and try again.',
  'invalid-request': 'Copilot could not open this request. Try again.',
  'invalid-destination': 'Copilot could not open this request. Try again.',
  navigation: 'Copilot could not open this request. Try again.',
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isSpace(value: unknown): value is AhpSpaceSelection {
  return (
    isRecord(value) &&
    typeof value.owner === 'string' &&
    typeof value.id === 'number' &&
    typeof value.slug === 'string' &&
    typeof value.name === 'string'
  )
}

function isProvisioningTarget(value: unknown): value is AhpProvisioningTarget {
  if (!isRecord(value)) return false
  if (value.kind === 'space') return isSpace(value.space)
  if (value.kind === 'scope') return typeof value.owner === 'string' && value.owner.trim() !== ''
  return (
    value.kind === 'repository' &&
    typeof value.nameWithOwner === 'string' &&
    value.nameWithOwner.trim() !== '' &&
    (value.scopeOwner === undefined || (typeof value.scopeOwner === 'string' && value.scopeOwner.trim() !== ''))
  )
}

function isReference(value: unknown): value is OneCopilotTaskReference {
  if (!isRecord(value) || typeof value.path !== 'string' || value.path.trim() === '') return false
  if (value.kind !== undefined && value.kind !== 'file' && value.kind !== 'snippet') return false
  if (value.repository !== undefined && (typeof value.repository !== 'string' || value.repository.trim() === '')) {
    return false
  }
  return (
    value.range === undefined ||
    (isRecord(value.range) && typeof value.range.start === 'number' && typeof value.range.end === 'number')
  )
}

function isPreparedPrompt(value: unknown): value is PreparedPrompt {
  if (!isRecord(value) || typeof value.text !== 'string') return false
  if (value.attachments === undefined) return true
  // Attachments are handed to the destination composer and on to the model, so
  // a replayed record only counts as a prompt when each one is at least an
  // object with the protocol's discriminator.
  return (
    Array.isArray(value.attachments) && value.attachments.every(item => isRecord(item) && typeof item.type === 'string')
  )
}

function isTarget(value: unknown): value is StoredOneCopilotEntryTarget {
  if (!isRecord(value) || (value.kind !== 'chat' && value.kind !== 'task')) return false
  if (value.kind === 'chat') {
    const hasProvisioningTarget = value.provisioningTarget !== undefined
    const hasLegacySpace = value.space !== undefined
    if (hasProvisioningTarget && hasLegacySpace) return false
    if (hasProvisioningTarget) return isProvisioningTarget(value.provisioningTarget)
    return !hasLegacySpace || isSpace(value.space)
  }

  if (value.repository !== undefined) {
    if (!isRecord(value.repository) || typeof value.repository.nameWithOwner !== 'string') return false
    if (value.repository.branch !== undefined && typeof value.repository.branch !== 'string') return false
  }
  return value.reference === undefined || isReference(value.reference)
}

function isEntryRequest(value: unknown): value is StoredOneCopilotEntryRequest {
  if (
    !isRecord(value) ||
    !isTarget(value.target) ||
    !isPreparedPrompt(value.prompt) ||
    typeof value.source !== 'string' ||
    value.source.trim() === ''
  ) {
    return false
  }
  if (value.behavior === 'prefill') return true
  return value.behavior === 'submit' && typeof value.inputId === 'string' && value.inputId.length > 0
}

function isCurrentEntryRequest(value: unknown): value is OneCopilotEntryRequest {
  return (
    isEntryRequest(value) &&
    !(value.target.kind === 'chat' && 'space' in value.target) &&
    (value.startupConfiguration === undefined ||
      (isRecord(value.startupConfiguration) && isSessionApprovalMode(value.startupConfiguration.approvalMode)))
  )
}

function isEntryHandoff(value: unknown): value is StoredOneCopilotEntryHandoff {
  if (
    !isRecord(value) ||
    (value.version !== 1 && value.version !== 2 && value.version !== ONE_COPILOT_ENTRY_VERSION) ||
    typeof value.id !== 'string' ||
    value.id.length === 0 ||
    typeof value.createdAt !== 'number' ||
    typeof value.expiresAt !== 'number' ||
    (value.owner !== undefined && typeof value.owner !== 'string') ||
    value.owner !== getCurrentUserLogin()
  ) {
    return false
  }

  if (value.version === ONE_COPILOT_ENTRY_VERSION) return isCurrentEntryRequest(value.request)
  return isEntryRequest(value.request) && !('startupConfiguration' in value.request)
}

function entryPayloadChars(entry: StoredOneCopilotEntryHandoff): number {
  return preparedPromptPayloadChars(entry.request.prompt) + entry.request.source.length
}

const entryStorage = createHandoffStorage<StoredOneCopilotEntryHandoff>({
  key: ONE_COPILOT_ENTRY_KEY,
  maxPayloadChars: MAX_ONE_COPILOT_ENTRY_PAYLOAD_CHARS,
  payloadChars: entryPayloadChars,
  validate: isEntryHandoff,
  getId: entry => entry.id,
  isStale: entry => entry.expiresAt <= Date.now(),
})

function newEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function requestIsValid(request: OneCopilotEntryRequest): boolean {
  if (!isCurrentEntryRequest(request) || request.source.trim() === '') return false
  if (request.target.kind === 'task' && request.target.repository?.nameWithOwner.trim() === '') return false
  return true
}

function normalizeStoredRequest(
  request: StoredOneCopilotEntryRequest | OneCopilotEntryRequest,
): OneCopilotEntryRequest {
  const target: OneCopilotEntryTarget =
    request.target.kind === 'chat' && 'space' in request.target
      ? {kind: 'chat', provisioningTarget: {kind: 'space', space: request.target.space}}
      : request.target

  if (request.behavior === 'submit') {
    return {
      target,
      prompt: request.prompt,
      source: request.source,
      ...(request.startupConfiguration ? {startupConfiguration: request.startupConfiguration} : {}),
      behavior: 'submit',
      inputId: request.inputId,
    }
  }
  return {
    target,
    prompt: request.prompt,
    source: request.source,
    ...(request.startupConfiguration ? {startupConfiguration: request.startupConfiguration} : {}),
    behavior: 'prefill',
  }
}

const SSR_URL_BASE = 'http://github.localhost'

function destinationUrl(destination: string, id: string): string | 'invalid-destination' {
  try {
    const base = typeof window === 'undefined' ? SSR_URL_BASE : window.location.href
    const url = new URL(destination, base)
    // A `javascript:`, `data:`, or `blob:` destination resolves to a URL whose
    // origin never matches the page, but the server has no page to compare
    // against, so the scheme is checked outright on both paths.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'invalid-destination'
    if (typeof window !== 'undefined' && url.origin !== window.location.origin) return 'invalid-destination'
    url.searchParams.set(ONE_COPILOT_ENTRY_QUERY_PARAM, id)
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return 'invalid-destination'
  }
}

/** Reads the entry id a destination URL was opened with, if any. */
export function readOneCopilotEntryId(search: string | URLSearchParams): string | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search
  const id = params.get(ONE_COPILOT_ENTRY_QUERY_PARAM)
  return id === null || id === '' ? null : id
}

/**
 * The same location without the entry marker, for the destination to put back
 * with `history.replaceState` once it has claimed (or failed to claim) the
 * request.
 *
 * A single-use marker left in the address bar is re-read on reload, and the
 * second read finds nothing — so a page the user simply refreshed would report
 * that their request had expired. Removing it makes a reload an ordinary visit.
 */
export function withoutOneCopilotEntryParam(location?: {pathname: string; search: string; hash: string}): string {
  const source = location ?? (typeof window === 'undefined' ? undefined : window.location)
  if (!source) return ''

  const params = new URLSearchParams(source.search)
  params.delete(ONE_COPILOT_ENTRY_QUERY_PARAM)
  const search = params.toString()
  return `${source.pathname}${search ? `?${search}` : ''}${source.hash}`
}

/**
 * Create the short URL marker and persist the rich prepared input behind it.
 * Callers that want to control routing can navigate to the returned URL.
 */
export function createOneCopilotEntry(
  request: OneCopilotEntryRequest,
  {destination = getOneCopilotAgentsPath(), now = Date.now()}: {destination?: string; now?: number} = {},
): OneCopilotEntryCreationResult {
  if (!requestIsValid(request)) return {status: 'failed', failure: 'invalid-request'}

  const id = newEntryId()
  const url = destinationUrl(destination, id)
  if (url === 'invalid-destination') return {status: 'failed', failure: url}

  const entry: OneCopilotEntryHandoff = {
    version: ONE_COPILOT_ENTRY_VERSION,
    id,
    createdAt: now,
    expiresAt: now + ONE_COPILOT_ENTRY_TTL_MS,
    owner: getCurrentUserLogin(),
    request,
  }
  const failure = entryStorage.write(entry)
  return failure ? {status: 'failed', failure} : {status: 'created', id, url}
}

/**
 * Store a command and navigate to its destination. The optional navigator keeps
 * the API usable by routers without making the transport depend on React.
 *
 * Use `behavior: 'prefill'` to let the user review the command, or
 * `behavior: 'submit', inputId: '...'` for an idempotent automatic submit.
 */
export function openOneCopilot(
  request: OneCopilotEntryRequest & {
    destination?: string
    navigate?: (url: string) => void
  },
): OneCopilotEntryCreationResult {
  const {destination, navigate} = request
  const normalizedRequest: OneCopilotEntryRequest =
    request.behavior === 'submit'
      ? {
          target: request.target,
          prompt: request.prompt,
          source: request.source,
          ...(request.startupConfiguration ? {startupConfiguration: request.startupConfiguration} : {}),
          behavior: 'submit',
          inputId: request.inputId,
        }
      : {
          target: request.target,
          prompt: request.prompt,
          source: request.source,
          ...(request.startupConfiguration ? {startupConfiguration: request.startupConfiguration} : {}),
          behavior: 'prefill',
        }
  const result = createOneCopilotEntry(normalizedRequest, destination === undefined ? {} : {destination})
  if (result.status === 'failed') return result

  const go = navigate ?? (typeof window === 'undefined' ? undefined : (url: string) => window.location.assign(url))
  if (go) {
    try {
      go(result.url)
    } catch {
      // The URL that would have claimed this record is never going to be
      // visited, so it is withdrawn by id: a plain clear would discard whatever
      // is stored, including a request a concurrent surface had just written.
      entryStorage.claim(result.id)
      return {status: 'failed', failure: 'navigation'}
    }
  }
  return result
}

export function claimOneCopilotEntry(id: string): OneCopilotEntryClaimResult {
  const result = entryStorage.claim(id)
  if (result.status !== 'claimed') return result
  return {status: 'claimed', id: result.value.id, request: normalizeStoredRequest(result.value.request)}
}

export function clearOneCopilotEntry(): boolean {
  return entryStorage.clear()
}
